/**
 * Shared emitters — source workflows call these after a successful save/activity.
 * Failed or unchanged saves must not call these helpers.
 */

import { formatChangeValues, CHANGE_HISTORY_DISPLAY } from './changeHistoryEntry';
import { recordChangeHistory } from './changeHistoryStore';

const valuesFromChangesArray = (changes = [], side) => {
  const map = {};
  (changes || []).forEach((row) => {
    const key = row.field || row.key;
    if (!key) return;
    map[key] = side === 'previous' ? row.previousValue : row.newValue;
  });
  return Object.keys(map).length ? map : null;
};

const valuesFromMaps = (map) => {
  if (map == null) return null;
  if (typeof map === 'string') return map;
  return map;
};

/**
 * Emit from keyword / pricing-style local history entry.
 */
export function emitSnapshotHistoryEntry({
  localEntry,
  module,
  sourceModuleRef,
  changedItem,
  itemId,
  changeReason,
}) {
  if (!localEntry || !itemId) return null;
  const changeType = localEntry.action || 'Edit';
  const iso = localEntry.on || localEntry.timestamp || new Date().toISOString();
  return recordChangeHistory({
    sourceChangeRef: `${sourceModuleRef}::${itemId}::${changeType}::${iso}`,
    sourceModuleRef,
    sourceItemRef: itemId,
    module,
    changedItem: changedItem || itemId,
    itemId,
    changeType: changeType === 'Created' ? 'Add' : changeType === 'Updated' ? 'Edit' : changeType,
    previousValues:
      changeType === 'Created' || changeType === 'Add'
        ? CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE_NEW_ITEM
        : valuesFromMaps(localEntry.previousValues),
    newValues: valuesFromMaps(localEntry.newValues),
    changeReason: changeReason || localEntry.changeReason || CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
    changedBy: localEntry.by
      ? { name: localEntry.by, role: 'Team Member' }
      : localEntry.actor || 'System',
    changedOnIso: iso,
  });
}

/**
 * Emit from Quote Matching style entry with changes[].
 */
export function emitFieldChangesHistoryEntry({
  localEntry,
  module,
  sourceModuleRef,
  changedItem,
  itemId,
}) {
  if (!localEntry || !itemId) return null;
  const changeType = localEntry.action || 'Edit';
  const iso = localEntry.timestamp || localEntry.on || new Date().toISOString();
  const previous =
    changeType === 'Add'
      ? CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE_NEW_ITEM
      : valuesFromChangesArray(localEntry.changes, 'previous');
  const next = valuesFromChangesArray(localEntry.changes, 'next');
  return recordChangeHistory({
    sourceChangeRef: `${sourceModuleRef}::${itemId}::${changeType}::${iso}`,
    sourceModuleRef,
    sourceItemRef: itemId,
    module,
    changedItem: changedItem || itemId,
    itemId,
    changeType,
    previousValues: previous,
    newValues: next,
    changeReason: localEntry.changeReason || CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
    changedBy: localEntry.actor || 'System',
    changedOnIso: iso,
  });
}

/**
 * Collapse same-timestamp configurationHistory field rows into one central Edit.
 */
export function emitConfigurationHistoryBatch({
  rows = [],
  module,
  sourceModuleRef,
  changedItem,
  itemId,
  changeReason,
  actor,
  stamp,
  changeType = 'Edit',
}) {
  if (!itemId || !(rows || []).length) {
    if (!itemId) return null;
    // Activity / state change without field rows
    const iso = stamp || new Date().toISOString();
    return recordChangeHistory({
      sourceChangeRef: `${sourceModuleRef}::${itemId}::${changeType}::${iso}`,
      sourceModuleRef,
      sourceItemRef: itemId,
      module,
      changedItem: changedItem || itemId,
      itemId,
      changeType,
      previousValues: null,
      newValues: null,
      activityOnly: true,
      activityDetails: `${changeType} recorded`,
      changeReason: changeReason || CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
      changedBy: actor || 'System',
      changedOnIso: iso,
    });
  }

  const previous = {};
  const next = {};
  rows.forEach((row) => {
    const field = row.field || row.key;
    if (!field) return;
    previous[field] = row.previousValue;
    next[field] = row.newValue;
  });
  const iso = stamp || rows[0]?.timestamp || new Date().toISOString();
  return recordChangeHistory({
    sourceChangeRef: `${sourceModuleRef}::${itemId}::${changeType}::${iso}`,
    sourceModuleRef,
    sourceItemRef: itemId,
    module,
    changedItem: changedItem || itemId,
    itemId,
    changeType,
    previousValues: previous,
    newValues: next,
    changeReason: changeReason || CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
    changedBy: actor || rows[0]?.actor || 'System',
    changedOnIso: iso,
  });
}

/**
 * Generic successful rule/config save emitter (for pass-through workflows).
 */
export function emitRuleSaveHistory({
  isNew = false,
  previous,
  next,
  module,
  sourceModuleRef,
  changedItem,
  itemId,
  changeReason,
  actor,
  stamp,
  compareKeys = [],
  conditionsKey = 'conditions',
}) {
  if (!next || !itemId) return null;
  const iso = stamp || next.updatedOn || next.lastUpdatedOn || new Date().toISOString();
  const changeType = isNew ? 'Add' : 'Edit';

  if (isNew) {
    const initial = {};
    (compareKeys.length ? compareKeys : Object.keys(next)).forEach((key) => {
      if (['changeHistory', 'configurationHistory', 'testHistory', 'changeReason'].includes(key)) {
        return;
      }
      if (next[key] != null && typeof next[key] !== 'object') initial[key] = next[key];
      if (key === conditionsKey) {
        initial.Conditions = formatChangeValues(
          Array.isArray(next[conditionsKey])
            ? next[conditionsKey].map((c) => JSON.stringify(c)).join(' AND ')
            : next[conditionsKey]
        );
      }
    });
    return recordChangeHistory({
      sourceChangeRef: `${sourceModuleRef}::${itemId}::Add::${iso}`,
      sourceModuleRef,
      sourceItemRef: itemId,
      module,
      changedItem: changedItem || next.ruleName || next.name || itemId,
      itemId,
      changeType: 'Add',
      previousValues: CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE_NEW_ITEM,
      newValues: Object.keys(initial).length ? initial : { Status: next.status || next.ruleStatus || 'Active' },
      changeReason: changeReason || next.changeReason || CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
      changedBy: actor || 'System',
      changedOnIso: iso,
    });
  }

  if (!previous) return null;

  const previousValues = {};
  const newValues = {};
  const keys =
    compareKeys.length > 0
      ? compareKeys
      : [
          'ruleName',
          'name',
          'status',
          'ruleStatus',
          'configurationStatus',
          conditionsKey,
          'retentionPeriod',
          'allowedMaximum',
          'priority',
        ];

  keys.forEach((key) => {
    if (!(key in next) && !(key in previous)) return;
    const before = previous[key];
    const after = next[key];
    const beforeText =
      key === conditionsKey
        ? formatConditions(before)
        : before == null || before === ''
          ? CHANGE_HISTORY_DISPLAY.NONE
          : String(typeof before === 'object' ? JSON.stringify(before) : before);
    const afterText =
      key === conditionsKey
        ? formatConditions(after)
        : after == null || after === ''
          ? CHANGE_HISTORY_DISPLAY.NONE
          : String(typeof after === 'object' ? JSON.stringify(after) : after);
    if (beforeText === afterText) return;
    const label = key === conditionsKey ? 'Conditions' : key;
    previousValues[label] = beforeText;
    newValues[label] = afterText;
  });

  if (!Object.keys(newValues).length) return null;

  return recordChangeHistory({
    sourceChangeRef: `${sourceModuleRef}::${itemId}::Edit::${iso}`,
    sourceModuleRef,
    sourceItemRef: itemId,
    module,
    changedItem: changedItem || next.ruleName || next.name || itemId,
    itemId,
    changeType: 'Edit',
    previousValues,
    newValues,
    changeReason: changeReason || next.changeReason || CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
    changedBy: actor || 'System',
    changedOnIso: iso,
  });
}

function formatConditions(conditions) {
  if (!Array.isArray(conditions)) {
    return conditions == null || conditions === ''
      ? CHANGE_HISTORY_DISPLAY.NONE
      : String(conditions);
  }
  if (!conditions.length) return 'Conditions: (none)';
  return `Conditions: ${conditions
    .map((c) => {
      if (typeof c === 'string') return c;
      const parts = [c.field || c.issueType || c.attribute, c.operator, c.value || c.equals || c.tradeCategoryId]
        .filter(Boolean)
        .join(' · ');
      return parts || JSON.stringify(c);
    })
    .join(' AND ')}`;
}

export function emitSettingsChange({
  section,
  subsection,
  previousValues,
  newValues,
  changeReason,
  actor,
  sourceItemRef,
}) {
  const iso = new Date().toISOString();
  const item = subsection ? `${section} — ${subsection}` : section;
  const ref = sourceItemRef || item.toLowerCase().replace(/\s+/g, '_');
  return recordChangeHistory({
    sourceChangeRef: `settings::${ref}::Edit::${iso}`,
    sourceModuleRef: 'settings',
    sourceItemRef: ref,
    module: 'Settings',
    changedItem: item,
    itemId: CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE,
    changeType: 'Edit',
    previousValues,
    newValues,
    changeReason: changeReason || CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
    changedBy: actor || 'System',
    changedOnIso: iso,
  });
}

export function emitPublicationActivity({
  targetType,
  targetId,
  targetName,
  actionLabel,
  outcome,
  actor,
  module = 'Posts',
  sourceModuleRef = 'posts_management',
}) {
  const iso = new Date().toISOString();
  return recordChangeHistory({
    sourceChangeRef: `${sourceModuleRef}::${targetId}::${actionLabel}::${iso}`,
    sourceModuleRef,
    sourceItemRef: targetId,
    module,
    changedItem: targetName || targetId,
    itemId: targetId,
    changeType: actionLabel,
    activityOnly: true,
    activityDetails: outcome || `${actionLabel} recorded for ${targetType || 'item'}`,
    changeReason: CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
    changedBy: actor || 'System',
    changedOnIso: iso,
  });
}

/**
 * Trade Categories register — one parent entry per complete category save,
 * including included Subcategory changes (no separate Subcategory CHG id).
 */
export function emitTradeCategoryHistory({
  isAdd = false,
  previous = null,
  next,
  actor,
  stamp,
  changeReason,
  activityDetails,
  affectedSubcategoryIds = [],
}) {
  if (!next?.id) return null;
  const iso = stamp || next.lastUpdatedOn || new Date().toISOString();
  const changeType = isAdd ? 'Add' : 'Edit';

  const formatSubs = (category) => {
    const subs = Array.isArray(category?.tradeSubcategories)
      ? category.tradeSubcategories
      : [];
    if (!subs.length) return '(none)';
    return subs
      .map((sub) => {
        const name = sub?.name || sub?.id || '—';
        const id = sub?.id ? ` (${sub.id})` : '';
        const publicFacing = sub?.displayAsPublicFacing ? ' · Public-facing' : '';
        return `${name}${id}${publicFacing}`;
      })
      .join('; ');
  };

  if (isAdd) {
    return recordChangeHistory({
      sourceChangeRef: `trade_categories::${next.id}::Add::${iso}`,
      sourceModuleRef: 'trade_categories_management',
      sourceItemRef: next.id,
      module: 'Trade Categories',
      changedItem: next.name || next.id,
      itemId: next.id,
      changeType: 'Add',
      previousValues: CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE_NEW_ITEM,
      newValues: {
        'Trade Category': next.name || next.id,
        Subcategories: formatSubs(next),
        ...(affectedSubcategoryIds.length
          ? { 'Affected Subcategory IDs': affectedSubcategoryIds.join(', ') }
          : {}),
      },
      changeReason: changeReason || CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
      changedBy: actor || 'System',
      changedOnIso: iso,
      activityDetails: activityDetails || CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE,
    });
  }

  const previousValues = {};
  const newValues = {};
  if (previous) {
    const prevName = previous.name || '';
    const nextName = next.name || '';
    if (prevName !== nextName) {
      previousValues['Trade Category'] = prevName || CHANGE_HISTORY_DISPLAY.NONE;
      newValues['Trade Category'] = nextName || CHANGE_HISTORY_DISPLAY.NONE;
    }
    const prevIcon = previous.iconFileName || previous.iconUrl || '';
    const nextIcon = next.iconFileName || next.iconUrl || '';
    if (prevIcon !== nextIcon) {
      previousValues.Icon = prevIcon || CHANGE_HISTORY_DISPLAY.NONE;
      newValues.Icon = nextIcon || CHANGE_HISTORY_DISPLAY.NONE;
    }
    const prevSubs = formatSubs(previous);
    const nextSubs = formatSubs(next);
    if (prevSubs !== nextSubs) {
      previousValues.Subcategories = prevSubs;
      newValues.Subcategories = nextSubs;
    }
  }

  if (!Object.keys(newValues).length) {
    if (activityDetails) {
      return recordChangeHistory({
        sourceChangeRef: `trade_categories::${next.id}::${changeType}::${iso}`,
        sourceModuleRef: 'trade_categories_management',
        sourceItemRef: next.id,
        module: 'Trade Categories',
        changedItem: next.name || next.id,
        itemId: next.id,
        changeType,
        activityOnly: true,
        activityDetails,
        changeReason: changeReason || CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
        changedBy: actor || 'System',
        changedOnIso: iso,
      });
    }
    return null;
  }

  if (affectedSubcategoryIds.length) {
    newValues['Affected Subcategory IDs'] = affectedSubcategoryIds.join(', ');
  }

  return recordChangeHistory({
    sourceChangeRef: `trade_categories::${next.id}::Edit::${iso}`,
    sourceModuleRef: 'trade_categories_management',
    sourceItemRef: next.id,
    module: 'Trade Categories',
    changedItem: next.name || next.id,
    itemId: next.id,
    changeType: 'Edit',
    previousValues,
    newValues,
    changeReason: changeReason || CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
    changedBy: actor || 'System',
    changedOnIso: iso,
    activityDetails: activityDetails || CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE,
  });
}
